/**
 * BlockReasonSelector
 * 
 * UX Principle #7: Exceptions Are First-Class
 * 
 * Required Exception Paths:
 * - External dependency
 * - Missing resource
 * - Conflicting signal
 * - Invalid precondition
 * - Insufficient authority
 * 
 * UX Rule: Signaling an exception must be as easy as confirming completion.
 */

import React from 'react';
import { BlockReason, BLOCK_REASON_LABELS } from '../../types/executionSignal';
import {
  GlobalOutlined,
  InboxOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  LockOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';

interface BlockReasonSelectorProps {
  value: BlockReason | null;
  onChange: (reason: BlockReason | null) => void;
  disabled?: boolean;
}

const REASON_ICONS: Record<BlockReason, React.ReactNode> = {
  external_dependency: <GlobalOutlined />,
  missing_resource: <InboxOutlined />,
  conflicting_signal: <WarningOutlined />,
  invalid_precondition: <ExclamationCircleOutlined />,
  insufficient_authority: <LockOutlined />,
  other: <QuestionCircleOutlined />
};

const REASON_DESCRIPTIONS: Record<BlockReason, string> = {
  external_dependency: 'Waiting on something outside the system',
  missing_resource: 'Required resource or information is unavailable',
  conflicting_signal: 'This conflicts with another requirement',
  invalid_precondition: 'Prerequisites have not been met',
  insufficient_authority: 'Requires higher authorization level',
  other: 'Another reason not listed above'
};

const BLOCK_REASONS: BlockReason[] = [
  'external_dependency',
  'missing_resource',
  'conflicting_signal',
  'invalid_precondition',
  'insufficient_authority',
  'other'
];

export const BlockReasonSelector: React.FC<BlockReasonSelectorProps> = ({
  value,
  onChange,
  disabled = false
}) => {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '8px'
    }}>
      {BLOCK_REASONS.map((reason) => {
        const isSelected = value === reason;
        
        return (
          <button
            key={reason}
            onClick={() => onChange(isSelected ? null : reason)}
            disabled={disabled}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '12px',
              backgroundColor: isSelected ? '#fef2f2' : 'white',
              border: `2px solid ${isSelected ? '#dc2626' : '#e5e7eb'}`,
              borderRadius: '8px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              transition: 'all 0.15s ease',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              if (!disabled && !isSelected) {
                e.currentTarget.style.borderColor = '#fca5a5';
                e.currentTarget.style.backgroundColor = '#fff5f5';
              }
            }}
            onMouseLeave={(e) => {
              if (!disabled && !isSelected) {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.backgroundColor = 'white';
              }
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '4px'
            }}>
              <span style={{
                color: isSelected ? '#dc2626' : '#6b7280',
                fontSize: '16px'
              }}>
                {REASON_ICONS[reason]}
              </span>
              <span style={{
                fontSize: '13px',
                fontWeight: 600,
                color: isSelected ? '#dc2626' : '#374151'
              }}>
                {BLOCK_REASON_LABELS[reason]}
              </span>
            </div>
            <span style={{
              fontSize: '11px',
              color: '#6b7280',
              lineHeight: 1.4
            }}>
              {REASON_DESCRIPTIONS[reason]}
            </span>
          </button>
        );
      })}
    </div>
  );
};
